/**
 * ApiKeysPage.tsx — /app/api-keys — API key management
 *
 * Purpose:    List keys, create (free tier: read-only scope only), one-time
 *             key reveal with copy button, revoke with confirm.
 * Inputs:     apiKeysApi + billingApi from @/lib/saas-client
 * Outputs:    API key management UI + curl usage example
 * Constraints: Dark-first styling; WCAG AA
 * SOT:        nsentry SaaS app zone
 */
import { useEffect, useState, type FormEvent } from 'react'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { apiKeysApi, billingApi, type ApiKey } from '@/lib/saas-client'
import { TIERS, type TierId } from '@/lib/tiers'
import type { SentryError } from '@/lib/sentry-error'

export function ApiKeysPage() {
  const [state, setState] = useState<AsyncState>('loading')
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [tier, setTier] = useState<TierId>('free')
  const [error, setError] = useState<SentryError | undefined>()
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [scope, setScope] = useState<'read' | 'full'>('read')
  const [formError, setFormError] = useState<string | null>(null)
  const [revealedKey, setRevealedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  async function load() {
    setState('loading')
    const [keysRes, billingRes] = await Promise.all([apiKeysApi.list(), billingApi.get()])
    if (!keysRes.ok) {
      setError(keysRes.error)
      setState('error')
      return
    }
    setKeys(keysRes.value)
    if (billingRes.ok) setTier(billingRes.value.tier)
    setState(keysRes.value.length === 0 ? 'empty' : 'success')
  }

  useEffect(() => {
    void load()
  }, [])

  const fullAccessAllowed = TIERS[tier].apiAccess === 'full'

  async function handleCreate(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    const result = await apiKeysApi.create({ name, scope })
    if (!result.ok) {
      setFormError(result.error.message)
      return
    }
    setRevealedKey(result.value.key)
    setShowForm(false)
    setName('')
    setScope('read')
    await load()
  }

  async function handleRevoke(id: string) {
    if (!confirm('Revoke this API key? Anything using it will stop working immediately.')) return
    await apiKeysApi.remove(id)
    await load()
  }

  async function copyKey() {
    if (!revealedKey) return
    await navigator.clipboard.writeText(revealedKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-brand-ink">API Keys</h1>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
        >
          Create key
        </button>
      </div>

      {revealedKey && (
        <div className="mb-6 rounded-xl border border-sky-200 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-950/40 p-5" role="alert">
          <p className="mb-2 text-sm font-semibold text-sky-700 dark:text-sky-300">Save this key now — it will not be shown again.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md bg-brand-bg px-3 py-2 text-sm text-gray-200">{revealedKey}</code>
            <button
              type="button"
              onClick={copyKey}
              className="rounded-md border border-brand-card px-3 py-2 text-xs font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p className="mt-3 text-xs text-brand-muted">Example usage:</p>
          <pre className="mt-1 overflow-x-auto rounded-md bg-brand-bg p-3 text-xs text-brand-secondary">
{`curl -H "Authorization: Bearer ${revealedKey}" \\
  https://api.sentry.nself.org/v1/monitors`}
          </pre>
          <button type="button" onClick={() => setRevealedKey(null)} className="mt-3 text-xs text-brand-muted hover:text-gray-200">
            Dismiss
          </button>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="mb-6 space-y-4 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Create API key">
          {formError && <p className="text-sm text-red-700 dark:text-red-300" role="alert">{formError}</p>}
          <div>
            <label htmlFor="key-name" className="mb-1 block text-sm font-medium text-brand-secondary">Name</label>
            <input
              id="key-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="CI pipeline"
              className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="key-scope" className="mb-1 block text-sm font-medium text-brand-secondary">Scope</label>
            <select
              id="key-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as 'read' | 'full')}
              className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none"
            >
              <option value="read">Read-only</option>
              <option value="full" disabled={!fullAccessAllowed}>Full access</option>
            </select>
            {!fullAccessAllowed && (
              <p className="mt-1 text-xs text-brand-muted">Full API access requires ɳSentry Bundle.</p>
            )}
          </div>
          <div className="flex gap-3">
            <button type="submit" className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">
              Create
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-md border border-brand-card px-4 py-2 text-sm font-medium text-brand-secondary hover:bg-brand-surface-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      <AsyncScreen
        state={state}
        error={error}
        onRetry={load}
        emptyMessage="No API keys yet"
        emptyAction={
          <button type="button" onClick={() => setShowForm(true)} className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500">
            Create your first key
          </button>
        }
      >
        <div className="overflow-x-auto rounded-xl border border-brand-subtle">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-surface text-brand-muted">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">Name</th>
                <th scope="col" className="px-4 py-3 font-medium">Key</th>
                <th scope="col" className="px-4 py-3 font-medium">Scope</th>
                <th scope="col" className="px-4 py-3 font-medium">Created</th>
                <th scope="col" className="px-4 py-3 font-medium"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800 bg-brand-bg">
              {keys.map((k) => (
                <tr key={k.id}>
                  <td className="px-4 py-3 font-medium text-brand-ink">{k.name}</td>
                  <td className="px-4 py-3 font-mono text-brand-muted">{k.prefix}…</td>
                  <td className="px-4 py-3 text-brand-muted">{k.scope}</td>
                  <td className="px-4 py-3 text-brand-muted">{new Date(k.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => handleRevoke(k.id)} className="text-red-700 dark:text-red-400 hover:text-red-700 dark:text-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-400">
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AsyncScreen>
    </div>
  )
}
